import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import type { ContentLibraryItem } from "@/hooks/useContentLibrary";
import { resolveActiveSpace } from "@/hooks/useActiveSpace";

// ── Exported types ─────────────────────────────────────────────────────────────
// Shared between useFeedEngineData hooks and useFeedEngine processing logic.

export interface CategoryWeight {
  id: string;
  base_weight: number;
  onboarding_weight: number;
  max_per_feed: number;
  active: boolean;
}

export interface DismissedRow {
  content_id: string;
  child_id: string | null;
}

export interface SnoozeRow {
  content_id: string;
  child_id: string | null;
  snooze_until: string;
}

export interface InteractionRow {
  content_id: string;
  interaction_type: string;
  session_id: string;
  session_date: string;
  created_at: string;
}

export interface PinnedInstanceRow {
  id: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  content_library: {
    id: string;
    title: string | null;
    body: string;
    cta_action: string;
    card_type: string;
    pin_to_top: boolean;
    auto_dismiss_hours: number | null;
    active: boolean;
  } | null;
}

export interface OwnedSpaceRow {
  id: string;
  name: string;
}

export interface CollectionStepMeta {
  templateId: string;
  templateName: string;
  templateIcon: string;
  templateColor: string;
  stepOrder: number;
  stepCount: number;
  stepTitleOverride: string | null;
}

export type CollectionStepIndex = Map<string, CollectionStepMeta>; // keyed by content_library_id

// ── Helper (exported so useFeedEngine can import instead of duplicate) ─────────

export function startOfWeekStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
}

// ── Fetchers (shared by hooks + prefetch) ─────────────────────────────────────

async function fetchFeedCategoryWeights(): Promise<CategoryWeight[]> {
  const { data, error } = await supabase
    .from("feed_category_weights")
    .select("*")
    .eq("active", true);
  if (error) throw error;
  return (data as CategoryWeight[]) ?? [];
}

async function fetchContentLibraryActive(): Promise<ContentLibraryItem[]> {
  const { data, error } = await supabase
    .from("content_library")
    .select("*")
    .eq("active", true);
  if (error) throw error;
  // Video capture is deferred — drop any challenge cards that ask the user to
  // record a Shortie. They'd route past the locked picker straight into
  // ShortieCapture.
  return ((data as unknown as ContentLibraryItem[]) ?? []).filter(
    (c) => (c as { capture_type?: string }).capture_type !== "video",
  );
}

async function fetchDismissedContent(userId: string): Promise<DismissedRow[]> {
  const { data, error } = await supabase
    .from("dismissed_content")
    .select("content_id, child_id")
    .eq("user_id", userId);
  if (error) throw error;
  return (data as DismissedRow[])?.map((d) => ({
    content_id: d.content_id,
    child_id: d.child_id ?? null,
  })) ?? [];
}

async function fetchCardSnoozes(userId: string): Promise<SnoozeRow[]> {
  const { data, error } = await supabase
    .from("card_snoozes")
    .select("content_id, child_id, snooze_until")
    .eq("user_id", userId)
    .gt("snooze_until", new Date().toISOString());
  if (error) throw error;
  return (data as SnoozeRow[])?.map((s) => ({
    content_id: s.content_id,
    child_id: s.child_id ?? null,
    snooze_until: s.snooze_until,
  })) ?? [];
}

async function fetchFeedInteractions(userId: string): Promise<InteractionRow[]> {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split("T")[0];
  const { data, error } = await supabase
    .from("content_interactions")
    .select("content_id, interaction_type, session_id, session_date, created_at")
    .eq("user_id", userId)
    .gte("session_date", thirtyDaysAgoStr);
  if (error) throw error;
  return (data as InteractionRow[]) ?? [];
}

async function fetchPinnedInstances(userId: string): Promise<PinnedInstanceRow[]> {
  const { data, error } = await supabase
    .from("user_card_instances")
    .select(`
      id,
      payload,
      created_at,
      content_library:content_library_id (
        id, title, body, cta_action, card_type,
        pin_to_top, auto_dismiss_hours, active
      )
    `)
    .eq("user_id", userId)
    .is("read_at", null)
    .is("dismissed_at", null)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as PinnedInstanceRow[]) ?? [];
}

async function fetchOwnedSpace(userId: string): Promise<OwnedSpaceRow | null> {
  const { data, error } = await supabase
    .from("spaces")
    .select("id, name")
    .eq("owner_id", userId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as OwnedSpaceRow | null;
}

interface CollectionTemplateStepRow {
  id: string;
  content_library_id: string;
  collection_template_id: string;
  order_index: number;
  required: boolean;
  step_title_override: string | null;
  collection_templates: { name: string; icon: string; color: string } | null;
}

async function fetchCollectionStepIndex(
  userId: string,
): Promise<{
  stepIndex: CollectionStepIndex;
  boostedContentIds: Set<string>;
  startedTemplateIds: Set<string>;
  firstStepByTemplate: Map<string, string>;
}> {
  const [
    { data: stepsData, error: stepsError },
    { data: completionsData, error: completionsError },
    { data: inProgressData, error: inProgressError },
    { data: allStartedData, error: allStartedError },
  ] = await Promise.all([
    supabase
      .from("collection_template_steps")
      .select(`
        id,
        content_library_id,
        collection_template_id,
        order_index,
        required,
        step_title_override,
        collection_templates:collection_template_id (
          name,
          icon,
          color
        )
      `),
    // Plan note: plan said filter by user_id but collection_step_completions has no user_id —
    // the correct column is completed_by (verified in 20260414000000_theme_steps.sql).
    supabase
      .from("collection_step_completions")
      .select("step_id, collection_id")
      .eq("completed_by", userId),
    supabase
      .from("collections")
      .select("id, template_id")
      .eq("user_id", userId)
      .is("completed_at", null)
      .not("template_id", "is", null),
    // All started templates (in-progress + completed) for teaser injection dedup
    supabase
      .from("collections")
      .select("template_id")
      .eq("user_id", userId)
      .not("template_id", "is", null),
  ]);

  if (stepsError) throw stepsError;
  if (completionsError) throw completionsError;
  if (inProgressError) throw inProgressError;
  if (allStartedError) throw allStartedError;

  const steps = (stepsData as unknown as CollectionTemplateStepRow[]) ?? [];
  const completions =
    (completionsData as { step_id: string; collection_id: string }[]) ?? [];
  const inProgress =
    (inProgressData as { id: string; template_id: string }[]) ?? [];

  // Compute step count per template
  const stepCountByTemplate = new Map<string, number>();
  for (const step of steps) {
    stepCountByTemplate.set(
      step.collection_template_id,
      (stepCountByTemplate.get(step.collection_template_id) ?? 0) + 1,
    );
  }

  // Build stepIndex: content_library_id → CollectionStepMeta
  const stepIndex: CollectionStepIndex = new Map();
  for (const step of steps) {
    if (!step.collection_templates) continue;
    stepIndex.set(step.content_library_id, {
      templateId: step.collection_template_id,
      templateName: step.collection_templates.name,
      templateIcon: step.collection_templates.icon ?? '',
      templateColor: step.collection_templates.color ?? '',
      stepOrder: step.order_index,
      stepCount: stepCountByTemplate.get(step.collection_template_id) ?? 0,
      stepTitleOverride: step.step_title_override ?? null,
    });
  }

  // Group steps by template, sorted by order_index, for next-step lookup
  const stepsByTemplate = new Map<string, CollectionTemplateStepRow[]>();
  for (const step of steps) {
    const arr = stepsByTemplate.get(step.collection_template_id) ?? [];
    arr.push(step);
    stepsByTemplate.set(step.collection_template_id, arr);
  }
  for (const arr of stepsByTemplate.values()) {
    arr.sort((a, b) => a.order_index - b.order_index);
  }

  // First step per template (first required step, falling back to first by order_index)
  // Used as the card_snoozes proxy key for teaser frequency limiting.
  const firstStepByTemplate = new Map<string, string>();
  for (const [templateId, sortedSteps] of stepsByTemplate) {
    const firstRequired = sortedSteps.find((s) => s.required);
    const first = firstRequired ?? sortedSteps[0];
    if (first) firstStepByTemplate.set(templateId, first.content_library_id);
  }

  // All template IDs the user has ever started (in-progress or completed)
  const startedTemplateIds = new Set<string>(
    ((allStartedData as { template_id: string }[]) ?? [])
      .map((c) => c.template_id)
      .filter(Boolean),
  );

  // Index completed step IDs per collection instance
  const completedStepsByCollection = new Map<string, Set<string>>();
  for (const c of completions) {
    const set = completedStepsByCollection.get(c.collection_id) ?? new Set<string>();
    set.add(c.step_id);
    completedStepsByCollection.set(c.collection_id, set);
  }

  // Next uncompleted required step per in-progress collection; cap at 3 boosts total
  const boostedContentIds = new Set<string>();
  for (const col of inProgress) {
    if (boostedContentIds.size >= 3) break;
    const templateSteps = stepsByTemplate.get(col.template_id) ?? [];
    const completedSteps =
      completedStepsByCollection.get(col.id) ?? new Set<string>();
    const nextStep = templateSteps.find(
      (s) => s.required && !completedSteps.has(s.id),
    );
    if (nextStep) {
      boostedContentIds.add(nextStep.content_library_id);
    }
  }

  return { stepIndex, boostedContentIds, startedTemplateIds, firstStepByTemplate };
}

async function fetchHasCoParent(userId: string, activeSpaceId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from("space_members")
    .select("id", { count: "exact", head: true })
    .eq("space_id", activeSpaceId)
    .in("role", ["owner", "member"])
    .eq("status", "active")
    .neq("user_id", userId);
  if (error) {
    console.error("fetchHasCoParent:", error.message);
    return false;
  }
  return (count ?? 0) > 0;
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useFeedCategoryWeights() {
  const query = useQuery({
    queryKey: ["feed-category-weights"],
    staleTime: 10 * 60_000,
    queryFn: fetchFeedCategoryWeights,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useContentLibraryActive() {
  const query = useQuery({
    queryKey: ["content-library", "active"],
    staleTime: 5 * 60_000,
    queryFn: fetchContentLibraryActive,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useDismissedContent(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["dismissed-content", userId],
    staleTime: 60_000,
    enabled: !!userId,
    queryFn: () => fetchDismissedContent(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useCardSnoozes(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["card-snoozes", userId],
    staleTime: 60_000,
    enabled: !!userId,
    queryFn: () => fetchCardSnoozes(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useFeedInteractions(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["feed-interactions", userId],
    staleTime: 30_000,
    enabled: !!userId,
    queryFn: () => fetchFeedInteractions(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function usePinnedInstances(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["pinned-instances", userId],
    staleTime: 60_000,
    enabled: !!userId,
    queryFn: () => fetchPinnedInstances(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useOwnedSpace(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["owned-space", userId],
    staleTime: 5 * 60_000,
    enabled: !!userId,
    queryFn: () => fetchOwnedSpace(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useCollectionStepIndex(userId: string | undefined) {
  const query = useQuery({
    queryKey: ["collection-step-index", userId],
    staleTime: 60_000,
    enabled: !!userId,
    queryFn: () => fetchCollectionStepIndex(userId!),
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useHasCoParent(userId: string | null, activeSpaceId: string | null) {
  const query = useQuery({
    queryKey: ["has-co-parent", userId, activeSpaceId],
    queryFn: () => fetchHasCoParent(userId!, activeSpaceId!),
    enabled: !!userId && !!activeSpaceId,
    staleTime: 5 * 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

// Pre-warm the engine's input cache on auth, so the first Home visit of a
// session has cards in memory (skips skeleton). Idempotent — TanStack
// short-circuits prefetchQuery when the cached entry is still fresh.
export async function prefetchFeedEngineData(userId: string): Promise<void> {
  // resolveActiveSpace runs in parallel with other prefetches and seeds the
  // active-space cache; its result is then used for the hasCoParent prefetch.
  const [activeSpace] = await Promise.all([
    resolveActiveSpace(userId),
    queryClient.prefetchQuery({
      queryKey: ["feed-category-weights"],
      queryFn: fetchFeedCategoryWeights,
      staleTime: 10 * 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["content-library", "active"],
      queryFn: fetchContentLibraryActive,
      staleTime: 5 * 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["dismissed-content", userId],
      queryFn: () => fetchDismissedContent(userId),
      staleTime: 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["card-snoozes", userId],
      queryFn: () => fetchCardSnoozes(userId),
      staleTime: 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["feed-interactions", userId],
      queryFn: () => fetchFeedInteractions(userId),
      staleTime: 30_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["pinned-instances", userId],
      queryFn: () => fetchPinnedInstances(userId),
      staleTime: 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["owned-space", userId],
      queryFn: () => fetchOwnedSpace(userId),
      staleTime: 5 * 60_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["collection-step-index", userId],
      queryFn: () => fetchCollectionStepIndex(userId),
      staleTime: 60_000,
    }),
  ]);

  if (activeSpace) {
    await queryClient.prefetchQuery({
      queryKey: ["has-co-parent", userId, activeSpace.id],
      queryFn: () => fetchHasCoParent(userId, activeSpace.id),
      staleTime: 5 * 60_000,
    });
  }
}
